import React from "react";
import clsx from "clsx";
import {
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteVideoTrack,
} from "twilio-video";
import { makeStyles } from "@mui/styles";

import { Typography } from "@mui/material";
import AudioLevelIndicator from "../AudioLevelIndicator/AudioLevelIndicator";
import useParticipants from "../useParticipants/useParticipants";
import { participantNameExtractor } from "../../utils/common-functions";
import useIsTrackSwitchedOff from "../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff";
import useParticipantIsReconnecting from "../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting";
import useTrack from "../../hooks/useTrack/useTrack";
import AvatarIcon from "../../icons/AvatarIcon";
import usePublications from "../../hooks/usePublications/usePublications";

const useStyles = makeStyles(() => {
  return {
    container: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      height: 0,
      overflow: "hidden",
      marginBottom: "0.5em",
      "& video": {
        filter: "none",
        objectFit: "contain !important",
      },
      borderRadius: "4px",
      border: `2px solid rgb(245, 248, 255)`,
      paddingTop: `calc(${(9 / 16) * 100}% - 2px)`,
      background: "black",
    },
    innerContainer: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      "& video": {
        objectFit: "cover !important",
      },
    },
    infoContainer: {
      position: "absolute",
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      width: "100%",
      background: "transparent",
      top: 0,
    },
    avatarContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "black",
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
    },
    reconnectingContainer: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(40, 42, 43, 0.75)",
      zIndex: 1,
    },
    screenShareIconContainer: {
      background: "rgba(0, 0, 0, 0.5)",
      padding: "0.18em 0.3em",
      marginRight: "0.3em",
      display: "flex",
      "& path": {
        fill: "white",
      },
    },
    identity: {
      background: "rgba(0, 0, 0, 0.5)",
      color: "white",
      padding: "0.18em 0.3em",
      margin: 0,
      display: "flex",
      alignItems: "center",
    },
    infoRowBottom: {
      display: "flex",
      justifyContent: "space-between",
      position: "absolute",
      bottom: 0,
      left: 0,
    },
    typeography: {
      color: "white",
    },
    hideParticipant: {
      display: "none",
    },
    cursorPointer: {
      cursor: "pointer",
    },
  };
});

interface IParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  isLocalParticipant?: boolean;
  hideParticipant?: boolean;
  room: any;
}

const ParticipantInfo: React.FC<IParticipantInfoProps> = ({
  participant,
  onClick,
  isSelected,
  children,
  isLocalParticipant,
  hideParticipant,
  room,
}) => {
  const publications = usePublications(participant);
  const participants = useParticipants(room);

  const audioPublication = publications.find((p) => p.kind === "audio");
  const videoPublication = publications.find(
    (p) => !p.trackName.includes("screen") && p.kind === "video"
  );

  const isVideoEnabled = Boolean(videoPublication);
  const isScreenShareEnabled = publications.find((p) =>
    p.trackName.includes("screen")
  );

  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(
    videoTrack as LocalVideoTrack | RemoteVideoTrack
  );

  const audioTrack = useTrack(audioPublication) as
    | LocalAudioTrack
    | RemoteAudioTrack
    | undefined;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  // console.log(Array.from(participants[0].audioTracks.values()).map((x) => x.track));

  const classes = useStyles();

  return (
    <div
      className={clsx(classes.container, {
        [classes.hideParticipant]: hideParticipant,
        [classes.cursorPointer]: Boolean(onClick),
      })}
      onClick={onClick}
      data-cy-participant={participant?.identity}
    >
      <div className={classes.infoContainer}>
        <div className={classes.infoRowBottom}>
          <span className={classes.identity}>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography
              variant="body1"
              className={classes.typeography}
              component="span"
            >
              {participantNameExtractor(participant?.identity)}
              {isLocalParticipant && " (You)"}
            </Typography>
          </span>
        </div>
      </div>
      <div className={classes.innerContainer}>
        {(!isVideoEnabled ||
          isVideoSwitchedOff ||
          (participants?.length === 0 && isScreenShareEnabled)) && (
          <div className={classes.avatarContainer}>
            <AvatarIcon />
          </div>
        )}
        {isParticipantReconnecting && (
          <div className={classes.reconnectingContainer}>
            <Typography variant="body1" className={classes.typeography}>
              Reconnecting...
            </Typography>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default ParticipantInfo;
